import { useTranslation } from 'react-i18next'

import FlexCol from '@/components/shared/FlexCol'
import FlexRow from '@/components/shared/FlexRow'
import { getPriceWithMonetaryUnit, type MonetaryUnitString } from '@/utils/price'
import { cn } from '@/shared/utils'
import { Locales } from '@/utils/language'

import type { FC } from 'react'
import type { UserServiceOfferOption, UserTreatmentOption } from '@unni/api-client/contracts/service-offer'

export const OptionCard = ({ number, option }: { number: number; option: UserServiceOfferOption }) => {
  const { t, i18n } = useTranslation()
  const price = option.price.exchangeRateAppliedPrice || option.price
  const { currency, originalAmount, discountedAmount } = price
  const discountPercentage = option.price.discountPercentage
  const isDomesticServiceOffer = !option.price.exchangeRateAppliedPrice
  const isLocaleJapanese = (i18n.language as Locales) === Locales.JA_JP

  return (
    <FlexRow className="border border-gray-200 rounded-lg" px={16} py={16} id={option.id}>
      <span className="typo-subTitle1Bold text-gray-600 w-6 h-6">{number}</span>
      <FlexCol gap={16} className="flex-1">
        <FlexRow elementType="h3">{option.name}</FlexRow>
        <FlexRow>
          <OptionTreatments treatments={option.treatments} />
        </FlexRow>
        <FlexCol items="end">
          {discountPercentage > 50 ? (
            <span className="typo-body1Bold text-typography-description">{t('common.price.specialPrice')}</span>
          ) : (
            <FlexRow gap={4} items="center" className={cn(originalAmount === discountedAmount && 'hidden')}>
              <span className="typo-body1Bold text-typography-description">
                {Number(discountPercentage).toFixed(0)}% {isLocaleJapanese && ' off'}
              </span>
              <span
                className={cn(!isLocaleJapanese && 'line-through', 'typo-body1Regular text-typography-description')}
              >
                {isLocaleJapanese && '通常価格: '}
                {getPriceWithMonetaryUnit({
                  currency: currency as MonetaryUnitString,
                  locale: i18n.language as Locales,
                  price: originalAmount,
                })}
              </span>
            </FlexRow>
          )}
          <FlexRow className="typo-subTitle1Bold text-typography-primary">
            {getPriceWithMonetaryUnit({
              currency: currency as MonetaryUnitString,
              locale: i18n.language as Locales,
              price: discountedAmount,
            })}
          </FlexRow>
        </FlexCol>
        <FlexRow className={cn(isDomesticServiceOffer && 'hidden')} items="center" justify="end" full>
          <span className="typo-footer1Regular text-typography-description">
            {t('pages.eventDetail.eventDetailCurrencyExchangeInfo', {
              targetCurrency: currency,
              priceWithSymbol: getPriceWithMonetaryUnit({
                currency: option.price.currency as MonetaryUnitString,
                locale: i18n.language as Locales,
                price: option.price.discountedAmount,
              }),
            })}
          </span>
        </FlexRow>
      </FlexCol>
    </FlexRow>
  )
}

const OptionTreatments = ({ treatments }: { treatments: UserTreatmentOption[] }) => {
  return (
    <FlexCol gap={16}>
      {treatments.map((treatment, index) => (
        <TreatmentItem key={treatment.id} index={index} treatment={treatment} />
      ))}
    </FlexCol>
  )
}

export interface TreatmentProps {
  index: number
  treatment: UserTreatmentOption
}

const TreatmentItem: FC<TreatmentProps> = ({ index, treatment }) => {
  const { t } = useTranslation()

  const separatorClass = 'before:content-["/"] before:typo-body1Medium before:text-gray-400 before:mx-1'
  const isTreatmentAvailable = treatment.id ? 'text-blue-800' : ''
  const isUnitPropertyVisible = treatment.unitProperty ? '' : 'hidden'
  const isMaterialVisible = treatment.material ? '' : 'hidden'
  const isTimesVisible = treatment.times ? '' : 'hidden'

  return (
    <FlexRow
      className={cn(
        'typo-body1Medium text-gray-700 flex-wrap',
        index > 0 ? 'before:content-["+"] before:typo-body1Medium before:text-gray-700 before:mx-1' : '',
      )}
    >
      <span className={cn(isTreatmentAvailable)}>{treatment.name}</span>
      <span className={cn(isUnitPropertyVisible, separatorClass, 'text-blue-800 font-body1Bold')}>
        {treatment.unitProperty?.value}
        {treatment.unitProperty?.name}
      </span>
      {treatment.priceProperties?.map(({ name, values }) => (
        <span key={`${name}:${values.map((value) => value.name).join('+ ')}`} className={separatorClass}>
          {name}: {values.map((value) => value.name).join('+ ')}
        </span>
      ))}
      <span className={cn(isMaterialVisible, separatorClass, 'typo-body1Medium')}>{treatment.material?.name}</span>
      <span className={cn(isTimesVisible, separatorClass, 'typo-body1Medium')}>
        {t('pages.eventDetail.commonOptionProcedureCount', { times: treatment.times.value })}
      </span>
    </FlexRow>
  )
}

export const TreatmentItemInline = ({ treatments }: { treatments: UserTreatmentOption[] }) => {
  const { t } = useTranslation()

  const formatTreatmentValue = (treatment: UserTreatmentOption): string => {
    const treatmentValues: string[] = [treatment.name]

    if (treatment.unitProperty) {
      treatmentValues.push(`${treatment.unitProperty.value}${treatment.unitProperty.name}`)
    }

    if (treatment.priceProperties) {
      const priceDetails = treatment.priceProperties.map(
        ({ name, values }) => `${name}: ${values.map((value) => value.name).join('+ ')}`,
      )
      treatmentValues.push(...priceDetails)
    }

    if (treatment.material) {
      treatmentValues.push(treatment.material.name)
    }

    if (treatment.times) {
      treatmentValues.push(t('pages.eventDetail.commonOptionProcedureCount', { times: treatment.times.value }))
    }

    return treatmentValues.join(', ')
  }

  const values = treatments?.map(formatTreatmentValue) || []

  return (
    <div
      className={cn(
        'typo-body1Medium text-typography-description',
        'block w-full',
        'text-ellipsis overflow-hidden whitespace-nowrap',
      )}
    >
      {values.join(' + ')}
    </div>
  )
}
